import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Icon, P, Text } from '@mxenabled/mxui'

import {
  InformationTooltip,
  Loader,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useScreenSize,
  useWidgetContainerProvider,
} from '../../common'
import { useCashflowUiStore } from '../context/hooks'
import { CurrencyText } from '../../common'
import RemainingCash from './RemainingCash'
import { buildDynamicCopy } from '../../core/utils/Localization'

const CashflowWidgetContent = () => {
  const theme = useTheme()
  const { availableWidth } = useWidgetContainerProvider()
  const { isMobile } = useScreenSize()

  const { selectedAccountGuids } = useGlobalUiStore()
  const { cashflow: customCopy } = useGlobalCopyStore()
  const { daysUntilNextIncome, expensesTilNextIncome, selectedCashAccounts, selectedCashBalance } =
    useCashflowUiStore()
  const { isDataLoaded } = useRecurringTransactionsStore()

  // Plain derivations: observer tracks the observable reads and re-renders
  // correctly even when arrays are mutated in place by MobX. The account-scoped
  // balance is a store computed (selectedCashBalance).
  const filteredExpenseTotal = expensesTilNextIncome
    .filter((expense) => selectedAccountGuids.includes(expense.accountGuid || ''))
    .reduce((acc, curr) => acc + curr.amount, 0)

  const availableToSpend = selectedCashBalance - filteredExpenseTotal

  if (!isDataLoaded) {
    return <Loader height={isMobile ? 210 : 120} />
  }

  return (
    <Box className="mx-cashflow" pl={16} pr={16} pt={16}>
      {availableWidth > 710 ? (
        <Stack
          border={`1px solid ${theme.palette.divider}`}
          borderRadius={1}
          direction="row"
          justifyContent="space-between"
          px={24}
        >
          <Stack pb={14} pt={12}>
            <CurrencyText amount={selectedCashBalance} variant="h2" />
            <P variant="caption" whiteSpace="nowrap">
              {
                buildDynamicCopy(
                  customCopy.total_cash_accounts,
                  selectedCashAccounts.length,
                ) as string
              }
            </P>
          </Stack>

          <Stack pt={18}>
            <Icon name="remove" />
          </Stack>

          <Stack pb={14} pt={12}>
            <CurrencyText amount={filteredExpenseTotal} variant="h2" />
            <P variant="caption" whiteSpace="nowrap">
              {buildDynamicCopy(customCopy.expenses_due, expensesTilNextIncome.length) as string}
            </P>
          </Stack>

          <Stack pt={18}>
            <Icon name="equal" />
          </Stack>

          <Stack pb={14} pr={24} pt={12}>
            <CurrencyText amount={availableToSpend} variant="h2" />
            <Stack alignItems="center" direction="row" gap={8}>
              <P variant="caption" whiteSpace="nowrap">
                {customCopy.available_to_spend}
              </P>
              <InformationTooltip body={customCopy.tooltip_body} title={customCopy.tooltip_title} />
            </Stack>
          </Stack>

          <Stack borderLeft={`1px solid ${theme.palette.divider}`} pb={12} pl={24} pt={12}>
            <>
              <Text variant="h2">
                {buildDynamicCopy(customCopy.days, daysUntilNextIncome) as string}
              </Text>
              <P variant="caption" whiteSpace="nowrap">
                {customCopy.next_paycheck}
              </P>
            </>
          </Stack>
        </Stack>
      ) : (
        <Stack border={`1px solid ${theme.palette.divider}`} borderRadius={1}>
          <Stack direction="row" justifyContent="space-between" p={16}>
            <Text variant="body2">
              {
                buildDynamicCopy(
                  customCopy.total_cash_accounts,
                  selectedCashAccounts.length,
                ) as string
              }
            </Text>
            <CurrencyText amount={selectedCashBalance} bold={true} variant="caption" />
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="space-between" p={16}>
            <Text variant="body2">
              {buildDynamicCopy(customCopy.expenses_due, expensesTilNextIncome.length) as string}
            </Text>
            <CurrencyText amount={filteredExpenseTotal} bold={true} symbol="-" variant="caption" />
          </Stack>

          <Divider />

          <Stack
            alignItems="center"
            bgcolor="background.default"
            direction="row"
            gap={8}
            height={48}
            justifyContent="space-between"
            px={16}
            sx={{ borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}
          >
            <RemainingCash remaining={availableToSpend} />
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between" p={16}>
            <Text variant="body2">{customCopy.next_paycheck}</Text>
            <Text bold={true} variant="caption">
              {buildDynamicCopy(customCopy.days, daysUntilNextIncome) as string}
            </Text>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}

export default observer(CashflowWidgetContent)
